import { useEffect, useCallback, useRef, useState } from 'react';
import { msalInstance } from '../index';
import { signalRService, type UploadProgress, type ProgressCallback, type CompletionCallback, type ErrorCallback } from '../services/signalRService';

export interface UseSignalROptions {
  serviceName: string; // e.g., 'Contracts', 'Configuration', etc.
  uploadType?: string; // e.g., 'Drg', 'Cpt', etc. - defaults to 'Drg' for backward compatibility
  onProgress?: ProgressCallback;
  onCompleted?: CompletionCallback;
  onError?: ErrorCallback;
  autoConnect?: boolean;
}

export interface UseSignalRReturn {
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export const useSignalR = (options: UseSignalROptions): UseSignalRReturn => {
  const { serviceName, uploadType = 'Drg', onProgress, onCompleted, onError, autoConnect = true } = options;
  
  // Validate required serviceName parameter
  if (!serviceName) {
    throw new Error('useSignalR: serviceName is required');
  }

  // State to track connection status
  const [isConnected, setIsConnected] = useState(false);

  // Function to get access token using MSAL
  const getAccessToken = useCallback(async () => {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) {
        console.log('No active MSAL account found for SignalR');
        return undefined;
      }

      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: [`${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/InternalApi`], // Use same scope as API calls
      });

      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token for SignalR:', error);
      return undefined;
    }
  }, []);

  // Use refs to avoid re-registering callbacks on every render
  const progressCallbackRef = useRef(onProgress);
  const completedCallbackRef = useRef(onCompleted);
  const errorCallbackRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    progressCallbackRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    completedCallbackRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    errorCallbackRef.current = onError;
  }, [onError]);

  const connect = useCallback(async () => {
    if (!serviceName) {
      console.warn('useSignalR: Cannot connect - serviceName is undefined');
      setIsConnected(false);
      return;
    }

    console.log(`useSignalR: Starting connection process for ${serviceName}...`);
    try {
      console.log(`useSignalR: Getting access token for ${serviceName}...`);
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        console.error(`useSignalR: No access token available for ${serviceName}`);
        setIsConnected(false);
        return;
      }

      console.log(`useSignalR: Access token obtained for ${serviceName}, connecting...`);
      
      // The factory, not the token: it is called again on every reconnect (see signalRService).
      await signalRService.connect(serviceName, getAccessToken);
      console.log(`useSignalR: SignalR connection successful for ${serviceName}`);
      setIsConnected(true);
      
      // Register callbacks for upload operations (all services can use generic callbacks)
      if (progressCallbackRef.current) {
        signalRService.onUploadProgress(serviceName, uploadType, progressCallbackRef.current);
      }
      if (completedCallbackRef.current) {
        signalRService.onUploadCompleted(serviceName, uploadType, completedCallbackRef.current);
      }
      if (errorCallbackRef.current) {
        signalRService.onUploadError(serviceName, uploadType, errorCallbackRef.current);
      }
      console.log(`useSignalR: Upload callbacks registered for ${serviceName} with uploadType: ${uploadType}`);
    } catch (error) {
      console.error(`useSignalR: Failed to connect to SignalR for ${serviceName}:`, error);
      setIsConnected(false);
    }
  }, [serviceName, getAccessToken]);

  const disconnect = useCallback(async () => {
    if (!serviceName) {
      console.warn('useSignalR: Cannot disconnect - serviceName is undefined');
      return;
    }

    // Unregister callbacks for upload operations (all services use generic callbacks)
    // Note: Method names must be lowercase to match how SignalR JavaScript client receives them
    const lowerUploadType = uploadType.toLowerCase();
    if (progressCallbackRef.current) {
      signalRService.off(serviceName, `${lowerUploadType}uploadprogress`, progressCallbackRef.current);
    }
    if (completedCallbackRef.current) {
      signalRService.off(serviceName, `${lowerUploadType}uploadcompleted`, completedCallbackRef.current);
    }
    if (errorCallbackRef.current) {
      signalRService.off(serviceName, `${lowerUploadType}uploaderror`, errorCallbackRef.current);
    }
    
    await signalRService.disconnect(serviceName);
    setIsConnected(false);
  }, [serviceName, uploadType]);

  // Auto-connect when component mounts
  useEffect(() => {
    console.log('useSignalR effect - autoConnect:', autoConnect, 'isConnected:', isConnected);
    if (autoConnect) {
      console.log('Attempting SignalR auto-connect...');
      connect();
    }

    return () => {
      if (autoConnect) {
        console.log('Cleaning up SignalR connection...');
        disconnect();
      }
    };
  }, [autoConnect, connect, disconnect]);

  return {
    isConnected,
    connect,
    disconnect
  };
};
